import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fabriquerLeDocument,
  rangerLeDocument,
} from '@groupe-trousseau/shared/api-client';
import { PDFDocument } from 'pdf-lib';
import { deballer } from '../../shared/api/client';
import { CLES } from '../../shared/donnees/cles';
import {
  exporterDesPages,
  type ElementDeTableau,
  type ImageDeTableau,
  type PageExportee,
  type PenseBeteDeTableau,
} from './moteur';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE DOCUMENT FABRIQUÉ DEPUIS LE TABLEAU — `CDC-2` F-25, ET D-001 POUR SON
 * ASSEMBLAGE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Le geste est en DEUX temps côté serveur, et c'est D-052 issue B** : « la fabrication
 * rend le nom et l'autorisation, puis une seconde route reçoit les octets et les range ».
 * Le serveur ne fabrique donc rien — **il n'a pas les octets** —, et ce fichier est la
 * moitié qui les fabrique.
 *
 * **Trois choses vivent ici et nulle part ailleurs :** l'assemblage des pages en un
 * document, le téléchargement, et l'enchaînement des deux appels. Ce sont **de la logique
 * métier**, pas de l'affichage : « une page ne contient pas de logique métier »
 * (`web/CLAUDE.md`), et l'ordre des deux appels porte une règle — la fabrication autorise,
 * le rangement suit, et **un rangement refusé n'annule pas le téléchargement**.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE D-001 DIT, CE QUE `pdf-lib` SAIT FAIRE, ET L'ÉCART ENTRE LES DEUX.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * D-001 : « l'export PDF assemble les **exports SVG de chaque frame triée par position
 * Y** avec `pdf-lib` ». Les deux pièces qu'elle nomme sont là — le port rend les exports
 * SVG des cases triées du haut vers le bas, `pdf-lib` assemble le document.
 *
 * **Mais `pdf-lib` ne sait pas ingérer un document SVG, et c'est lu dans ses types, pas
 * supposé.** Il expose `embedPng`, `embedJpg`, `embedPdf` et `drawSvgPath` — ce dernier
 * ne prend **qu'un attribut `d` de chemin**, donc ni texte, ni image, ni groupe, ni
 * couleur d'élément. Or le texte tapé est l'un des douze outils de F-22 et les images
 * sont F-24 : re-dessiner les chemins un par un **perdrait la moitié du tableau**.
 *
 * **Le SVG est donc tramé dans le navigateur avant d'être posé sur la page.** Ce que ça
 * coûte est nommé plutôt que caché : **le document est en points, pas en vecteurs**. Un
 * agrandissement au-delà de la résolution ci-dessous crénelle, là où un vrai vecteur non.
 * Ce que ça garde : les deux pièces nommées par D-001, aucune dépendance de plus, et
 * **tout ce qui est sur la page part au document** — le tracé, le texte, les formes.
 *
 * **La police n'est pas un point faible ici, et c'est vérifié dans la source du moteur** :
 * `exportToSvg` appelle `Fonts.generateFontFaceDeclarations`, qui **incorpore les polices
 * en `data:`** dans le SVG. L'image chargée par le navigateur est donc autonome — une
 * police servie par une adresse externe ne serait jamais chargée dans un `<img>`, et le
 * texte tomberait sur une police de repli sans que rien ne le dise.
 */

/**
 * **La résolution du tramage, en multiples du point PDF.**
 *
 * Une page A4 fait 595 × 842 **points**, l'unité du PDF, soit 72 par pouce. À 2, la page
 * est tramée à 144 points par pouce — assez pour de l'écriture manuscrite lue à l'écran
 * et imprimée sur A4, et un fichier qui reste envoyable.
 *
 * **Monter ce nombre multiplie le poids par son carré**, et le poids compte deux fois :
 * le document traverse le réseau vers le rangement, et il pèse sur le plafond de l'espace
 * de fichiers de la personne — un refus `PLAFOND_ESPACE_ATTEINT` est le symptôme qu'on
 * achèterait en montant ce nombre sans y penser.
 */
const RESOLUTION = 2;

/**
 * **La page du document est blanche, quel que soit le thème de qui la fabrique.**
 *
 * Le port exporte **sans fond** — `exportBackground: false` —, donc le SVG est
 * transparent. Une page transparente s'imprime blanche mais **se lit noire dans un
 * lecteur en thème sombre** : le fond est posé ici, une fois, sur le tramage.
 *
 * **Ce n'est pas une valeur d'interface**, donc pas un jeton de `tokens.css` : c'est du
 * contenu de document. Un jeton la ferait suivre le thème de l'écran, ce que la règle
 * interdit — la page reste blanche « y compris dans la structure sombre ».
 */
const FOND_DE_LA_PAGE = '#ffffff';

/** Ce que le geste a produit, tel que l'écran le dit à la personne. */
export type ResultatDuDocument = {
  /** Vide quand rien n'a été fabriqué faute de case non vide. */
  readonly nomDuFichier: string | null;
  /** Combien de pages sont parties. Zéro : le tableau n'a aucune case écrite. */
  readonly pages: number;
  /** « Il ne range que pour qui a un espace » (Q450) : faux pour un `orgadmin`. */
  readonly rangementPrevu: boolean;
  /** Vrai quand le document est entré dans l'espace de la personne. */
  readonly range: boolean;
  /** Pourquoi il n'y est pas entré, quand il n'y est pas entré. */
  readonly raison: 'PLAFOND_ESPACE_ATTEINT' | 'NOM_DEJA_PRIS' | null;
};

/**
 * **Une page tramée, en octets PNG.**
 *
 * **Le SVG passe par une adresse d'objet et non par une `data:` URL** : une `data:` URL
 * encode tout le document en base64 dans une chaîne, ce qui multiplie par quatre tiers un
 * texte qui porte déjà les polices incorporées.
 *
 * **Le canevas se salit si le SVG référence une adresse externe**, et `toBlob` lève alors
 * une erreur de sécurité au lieu de rendre des octets. Ça n'arrive pas aujourd'hui — les
 * polices sont incorporées et les images d'une scène voyagent dans `files`, donc en
 * `data:` elles aussi — et c'est écrit ici parce que le jour où une image de tableau
 * arrivera par une adresse signée (D-116), **c'est cette ligne qui rougira**, loin de sa
 * cause.
 */
async function tramer(page: PageExportee): Promise<ArrayBuffer> {
  const adresse = URL.createObjectURL(
    new Blob([page.svg], { type: 'image/svg+xml' }),
  );
  try {
    const image = await new Promise<HTMLImageElement>((tenir, rejeter) => {
      const chargee = new Image();
      chargee.onload = () => tenir(chargee);
      chargee.onerror = () =>
        rejeter(new Error('Une page du tableau n’a pas pu être dessinée.'));
      chargee.src = adresse;
    });

    const canevas = document.createElement('canvas');
    canevas.width = Math.round(page.largeur * RESOLUTION);
    canevas.height = Math.round(page.hauteur * RESOLUTION);
    const contexte = canevas.getContext('2d');
    if (contexte === null) {
      throw new Error('Ce navigateur ne sait pas dessiner le document.');
    }
    contexte.fillStyle = FOND_DE_LA_PAGE;
    contexte.fillRect(0, 0, canevas.width, canevas.height);
    contexte.drawImage(image, 0, 0, canevas.width, canevas.height);

    const octets = await new Promise<Blob | null>((tenir) => {
      canevas.toBlob(tenir, 'image/png');
    });
    if (octets === null) {
      throw new Error('Une page du tableau n’a pas pu être enregistrée.');
    }
    return await octets.arrayBuffer();
  } finally {
    URL.revokeObjectURL(adresse);
  }
}

/**
 * **Les pages, assemblées en un document — D-001.**
 *
 * **La page du PDF a exactement les dimensions de la case**, en points : c'est l'unité du
 * PDF comme celle de la grille du port, donc une case A4 devient une page A4 sans aucune
 * mise à l'échelle à décider ici.
 *
 * **L'ordre est celui que le port rend**, et il est celui du corpus : « le rang d'une case
 * est sa position verticale, et l'ordre du document est du haut vers le bas » (Q342).
 * Retrier ici ferait un second endroit qui décide de l'ordre des pages.
 */
export async function assemblerLeDocument(
  pages: readonly PageExportee[],
): Promise<Blob> {
  const document_ = await PDFDocument.create();
  for (const page of pages) {
    const image = await document_.embedPng(await tramer(page));
    const feuille = document_.addPage([page.largeur, page.hauteur]);
    feuille.drawImage(image, {
      x: 0,
      y: 0,
      width: page.largeur,
      height: page.hauteur,
    });
  }
  const octets = await document_.save();
  // **Les octets sont recopiés dans une vue neuve, et ce n'est pas de la superstition.**
  // `save()` rend un `Uint8Array` dont le tampon est déclaré `ArrayBufferLike`, ce qu'un
  // `Blob` refuse : il pourrait être partagé entre fils d'exécution. La recopie ferme le
  // type au lieu de le forcer par un `as`, **qui aurait affirmé une chose que rien ne
  // vérifie**.
  const contenu = new Uint8Array(octets.byteLength);
  contenu.set(octets);
  return new Blob([contenu], { type: 'application/pdf' });
}

/**
 * **Le téléchargement, et il a lieu AVANT le rangement — F-34 critère 3.**
 *
 * « Un document fabriqué alors que l'espace est plein est **téléchargé quand même** et
 * n'est pas rangé, et le produit le dit. » Télécharger après le rangement ferait dépendre
 * le geste principal d'un geste qui a le droit d'échouer.
 *
 * **L'adresse n'est pas révoquée tout de suite** : plusieurs navigateurs annulent un
 * téléchargement dont l'adresse d'objet disparaît avant qu'il ait commencé.
 */
function telecharger(nom: string, contenu: Blob): void {
  const adresse = URL.createObjectURL(contenu);
  const lien = document.createElement('a');
  lien.href = adresse;
  lien.download = nom;
  lien.rel = 'noopener';
  lien.style.display = 'none';
  document.body.appendChild(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(adresse), 60_000);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-25 — FABRIQUER LE DOCUMENT, LE TÉLÉCHARGER, ET LE RANGER.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Ce hook prend une FONCTION et non des pages**, et ce n'est pas un détour : le moteur
 * est le seul à savoir dessiner ses cases, la page est la seule à tenir ses commandes, et
 * **ce module ne doit connaître ni l'un ni l'autre**. Lui passer les pages toutes faites
 * obligerait l'écran à décider quoi faire d'un tableau vide — donc à porter une règle du
 * produit.
 *
 * **L'ORDRE DES QUATRE TEMPS PORTE TROIS RÈGLES.**
 *
 * 1. **On dessine d'abord, et on n'appelle le serveur que s'il y a quelque chose.** « Les
 *    cases vides sont écartées : le document n'a jamais de page blanche » (Q341) — et un
 *    tableau **entièrement** vide n'a donc aucune page. Un PDF sans page n'est pas un
 *    document : le geste s'arrête, et il le dit.
 * 2. **La fabrication autorise, et elle nomme.** C'est elle qui refuse une scène effacée
 *    (`SCENE_EFFACEE`) et qui rend le nom — « le nom du fichier porte la date du cours et
 *    l'instant de fabrication, et rien d'autre » (Q375). **Le nom ne se calcule pas ici** :
 *    il est calculé par le serveur, et celui de l'envoi n'est même pas lu au rangement.
 * 3. **Le téléchargement a lieu dans tous les cas**, avant le rangement.
 * 4. **Le rangement n'a lieu que si le serveur l'a annoncé.** « Seuls les comptes `tutor`
 *    et `student` ont un espace de fichiers » (Q437) : appeler la route pour un `orgadmin`
 *    rendrait un `403` de rôle sur un geste qui a réussi.
 *
 * **AUCUNE TRACE N'EST ÉCRITE PAR CE CHEMIN, ET C'EST UNE RÈGLE, PAS UN HASARD.** « La
 * fabrication ne laisse aucune trace » (Q399), **première dérogation du produit au régime
 * général**. Les pages sont dessinées depuis la scène **déjà montée dans le moteur** :
 * aucune relecture n'est demandée au serveur, donc aucune ligne d'audit `scene.relue`
 * n'est écrite.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LE DOCUMENT PORTE TOUS LES TABLEAUX DU COURS DEPUIS LE 20260829 — Q372.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-25 : « quand un cours a plusieurs tableaux, **le document les porte tous, dans
 * l'ordre où ils ont été ouverts** » (Q372). Le câblage de la veille n'en portait qu'un,
 * et la remontée disait pourquoi : « les porter tous demanderait de relire les scènes
 * précédentes, **et `relireUneScene` écrit une ligne d'audit à chaque appel** ».
 *
 * **Ce qui l'a fermé est la route de fabrication elle-même** : elle rend désormais le
 * contenu de tous les tableaux non effacés du cours. **Elle est le seul geste que le
 * corpus exempte de trace** (F-25 critère 3, Q399), donc servir les scènes depuis sa
 * réponse n'ouvre aucun chemin nouveau — là où un drapeau sur `relireUneScene` aurait
 * rendu DD-12 facultative pour tous ses lecteurs. Motif complet au DTO du serveur.
 *
 * **LE TABLEAU AFFICHÉ EST DESSINÉ DEPUIS LA SCÈNE MONTÉE, LES AUTRES DEPUIS LEURS
 * ÉLÉMENTS**, et l'asymétrie est un gain : la scène montée porte ce qui vient d'être
 * tracé, ses images et ses pense-bêtes, là où la réponse du serveur ne porte que les
 * tracés.
 *
 * **CE QUI MANQUAIT AU DOCUMENT EST SERVI DEPUIS LE 20260829** : sur les tableaux qu'on
 * n'affiche pas, **les images et les pense-bêtes partent aussi**. Les deux motifs qui
 * les tenaient dehors étaient périmés — D-122 fabrique l'adresse signée, D-123 a donné
 * aux pense-bêtes leur couleur et leur position, et l'export du moteur reçoit son dépôt
 * de fichiers en argument plutôt que d'une instance.
 *
 * **CE QUI RESTE VRAI, ET C'EST LE PRÉREQUIS DE D-122** : lire les pixels d'une image
 * exige que le contenant réponde `Access-Control-Allow-Origin`. Sans lui, l'image
 * s'affiche sur le tableau **et manque au document** — pour le tableau affiché comme
 * pour les autres. Le contenant local le fait ; ceux de la recette et de la production
 * ne sont pas réglés.
 */
/**
 * **Les pages de TOUS les tableaux du cours, dans l'ordre des rangs — Q372.**
 *
 * « Le document les porte tous, **dans l'ordre où ils ont été ouverts** » — et cet
 * ordre est celui des rangs, que le serveur rend déjà trié.
 *
 * **Le tableau affiché passe par la scène montée**, les autres par leurs éléments. Le
 * rang de chaque page est **renuméroté d'un bout à l'autre du document** : deux tableaux
 * qui portent chacun une page 1 donneraient sinon deux pages 1, et « la page 3 » cesserait
 * de désigner la même chose pour deux personnes qui se parlent (DD-05).
 */
async function pagesDuCours(
  tableaux: readonly {
    readonly tableauId: string;
    readonly traces: readonly { readonly contenu?: unknown }[];
    readonly penseBetes: readonly PenseBeteDeTableau[];
    readonly images: readonly ImageDeTableau[];
  }[],
  tableauAffiche: string,
  exporterLesPages: () => Promise<readonly PageExportee[]>,
): Promise<readonly PageExportee[]> {
  const toutes: PageExportee[] = [];
  for (const tableau of tableaux) {
    const pages =
      tableau.tableauId === tableauAffiche
        ? await exporterLesPages()
        : await exporterDesPages(
            tableau.traces.map((t) => t.contenu as ElementDeTableau),
            tableau.images,
            tableau.penseBetes,
          );
    for (const page of pages) {
      toutes.push({ ...page, rang: toutes.length + 1 });
    }
  }
  return toutes;
}

export function useDocumentDuTableau(tableauId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: async (
      exporterLesPages: () => Promise<readonly PageExportee[]>,
    ): Promise<ResultatDuDocument> => {
      /**
       * **L'AUTORISATION VIENT MAINTENANT EN PREMIER, ET C'EST Q372 QUI L'IMPOSE.**
       *
       * Ce hook exportait d'abord, pour n'appeler le serveur que s'il y avait quelque
       * chose à fabriquer. **Il ne peut plus** : le nombre de pages ne se connaît qu'une
       * fois les tableaux précédents reçus, et c'est cette réponse qui les porte.
       *
       * **L'appel supplémentaire ne coûte aucune trace** : F-25 critère 3 — « le système
       * doit n'écrire aucune ligne de trace lors d'une fabrication ». C'est la seule
       * raison pour laquelle l'ordre peut changer sans rien coûter.
       */
      const autorisation = await deballer(
        fabriquerLeDocument({ path: { id: tableauId } }),
      );

      const pages = await pagesDuCours(
        autorisation.tableaux,
        tableauId,
        exporterLesPages,
      );
      if (pages.length === 0) {
        return {
          nomDuFichier: null,
          pages: 0,
          rangementPrevu: false,
          range: false,
          raison: null,
        };
      }

      const contenu = await assemblerLeDocument(pages);
      telecharger(autorisation.nomDuFichier, contenu);

      if (!autorisation.rangementPrevu) {
        return {
          nomDuFichier: autorisation.nomDuFichier,
          pages: pages.length,
          rangementPrevu: false,
          range: false,
          raison: null,
        };
      }

      // **Le nom donné au fichier envoyé ne sert à rien, et c'est voulu** : « le nom est
      // calculé ici, et celui de l'envoi n'est pas lu. Ni désinfecté, ni conservé :
      // ignoré. » Il est repris tel quel pour que ce qui traverse le réseau porte le même
      // nom que ce qui a été téléchargé, et pour qu'un journal de requêtes reste lisible.
      const rangement = await deballer(
        rangerLeDocument({
          path: { id: tableauId },
          body: {
            fichier: new File([contenu], autorisation.nomDuFichier, {
              type: 'application/pdf',
            }),
          },
        }),
      );

      return {
        nomDuFichier: autorisation.nomDuFichier,
        pages: pages.length,
        rangementPrevu: true,
        range: rangement.range,
        // **`raison` est déclarée nullable au serveur et le client généré la rend NON
        // nullable** — `z.enum([...]).nullable()` sort bien `nullable: true` dans
        // `openapi.json`, et le générateur perd cette nullité **quand la propriété porte
        // un `enum`**, là où il la garde sur `fichierId`. Elle n'est donc lue que sur un
        // refus, où le serveur la garantit remplie, et le repli à vide dit ce que le type
        // ne dit pas.
        raison: rangement.range ? null : (rangement.raison ?? null),
      };
    },
    // **L'espace de fichiers a grossi**, et sa jauge est celle que F-34 montre. Rien
    // d'autre ne bouge : la fabrication n'écrit ni trace, ni objet document.
    onSuccess: async (resultat) => {
      if (resultat.range) {
        await cache.invalidateQueries({ queryKey: CLES.espace });
      }
    },
  });
}
